import {
  MAIL_TEMPLATE_KEYS,
  type EmailStatus,
  type MailMessage,
  type MailSuppression,
  type WebhookEventEntry,
} from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Input,
  Label,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@recipes/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatDate } from '@/features/shared/labels';
import { useDebounced } from '@/features/shared/recipe-picker';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

const STATUSES: EmailStatus[] = [
  'QUEUED',
  'SENT',
  'DELIVERED',
  'DELAYED',
  'BOUNCED',
  'COMPLAINED',
  'FAILED',
  'SUPPRESSED',
  'RECEIVED',
];

export const MAIL_STATUS_VARIANT: Record<
  EmailStatus,
  'default' | 'outline' | 'success' | 'warning' | 'destructive'
> = {
  QUEUED: 'outline',
  SENT: 'default',
  DELIVERED: 'success',
  DELAYED: 'warning',
  BOUNCED: 'destructive',
  COMPLAINED: 'destructive',
  FAILED: 'destructive',
  SUPPRESSED: 'warning',
  RECEIVED: 'outline',
};

const selectClass =
  'mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-2';

/** Admin → E-mail → Delivery: what happened to every mail, who is suppressed, what the provider told us. */
export function AdminMailDeliveryPage() {
  const { t } = useTranslation();
  return (
    <AdminPage section="mailDelivery" description={t('admin.mailDelivery.description')}>
      <Tabs defaultValue="log">
        <TabsList>
          <TabsTrigger value="log">{t('admin.mailDelivery.log')}</TabsTrigger>
          <TabsTrigger value="suppressions" data-testid="mail-tab-suppressions">
            {t('admin.mailDelivery.suppressions')}
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="mail-tab-events">
            {t('admin.mailDelivery.events')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="log">
          <DeliveryLog />
        </TabsContent>
        <TabsContent value="suppressions">
          <Suppressions />
        </TabsContent>
        <TabsContent value="events">
          <WebhookEvents />
        </TabsContent>
      </Tabs>
    </AdminPage>
  );
}

function LoadMore({
  query,
}: {
  query: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => unknown };
}) {
  const { t } = useTranslation();
  if (!query.hasNextPage) return null;
  return (
    <div className="mt-3 text-center">
      <Button
        variant="outline"
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      >
        {t('common.loadMore')}
      </Button>
    </div>
  );
}

function DeliveryLog() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState('');
  const [template, setTemplate] = useState('');
  const [to, setTo] = useState('');
  const params = {
    status: status || undefined,
    template: template || undefined,
    to: useDebounced(to.trim()) || undefined,
  };
  const log = useInfiniteQuery({
    queryKey: queryKeys.admin.mailMessages(params),
    queryFn: ({ pageParam }) =>
      api.admin.mail.messages({ ...params, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = log.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="mt-4">
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="mail-filter-status">{t('admin.mailDelivery.status')}</Label>
          <select
            id="mail-filter-status"
            className={selectClass}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">{t('admin.mailDelivery.all')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`admin.mailDelivery.statuses.${s}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="mail-filter-template">{t('admin.mailTemplates.template')}</Label>
          <select
            id="mail-filter-template"
            className={selectClass}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          >
            <option value="">{t('admin.mailDelivery.all')}</option>
            {MAIL_TEMPLATE_KEYS.map((key) => (
              <option key={key} value={key}>
                {t(`admin.mailTemplates.names.${key}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="mail-filter-to">{t('admin.mailDelivery.recipient')}</Label>
          <Input
            id="mail-filter-to"
            className="mt-1"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>
      {log.isLoading && <Spinner label={t('app.loading')} />}
      {log.error && <Alert variant="destructive">{describeError(log.error)}</Alert>}
      {log.data && (
        <ResponsiveTable<MailMessage>
          caption={t('admin.mailDelivery.log')}
          rows={rows}
          rowKey={(row) => row.id}
          empty={t('admin.mailDelivery.empty')}
          columns={[
            {
              key: 'date',
              header: t('admin.mailDelivery.date'),
              cell: (row) => formatDate(row.createdAt, i18n.language, true),
            },
            {
              key: 'template',
              header: t('admin.mailTemplates.template'),
              cell: (row) =>
                row.templateKey &&
                (MAIL_TEMPLATE_KEYS as readonly string[]).includes(row.templateKey)
                  ? t(`admin.mailTemplates.names.${row.templateKey}`)
                  : (row.templateKey ?? t('admin.mailDelivery.inbound')),
            },
            {
              key: 'to',
              header: t('admin.mailDelivery.recipient'),
              cell: (row) =>
                row.userId ? (
                  <Link
                    to={`/admin/users/${row.userId}`}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {row.toAddress}
                  </Link>
                ) : (
                  row.toAddress
                ),
            },
            {
              key: 'subject',
              header: t('admin.mailTemplates.subject'),
              cell: (row) => row.subject || '–',
            },
            {
              key: 'status',
              header: t('admin.mailDelivery.status'),
              cell: (row) => (
                <div>
                  <Badge variant={MAIL_STATUS_VARIANT[row.status]} data-testid="mail-log-status">
                    {t(`admin.mailDelivery.statuses.${row.status}`)}
                  </Badge>
                  {(row.error || row.incident) && (
                    <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                      {row.error ?? row.incident}
                    </p>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
      <LoadMore query={log} />
    </div>
  );
}

function Suppressions() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const list = useInfiniteQuery({
    queryKey: queryKeys.admin.mailSuppressions,
    queryFn: ({ pageParam }) => api.admin.mail.suppressions({ cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const lift = useMutation({
    mutationFn: (id: string) => api.admin.mail.liftSuppression(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'mail'] });
      toast.success(t('admin.mailDelivery.lifted'));
    },
    onError: (e) => toastError(e),
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="mt-4">
      <p className="mb-3 text-sm text-muted-foreground">
        {t('admin.mailDelivery.suppressionsHint')}
      </p>
      {list.isLoading && <Spinner label={t('app.loading')} />}
      {list.error && <Alert variant="destructive">{describeError(list.error)}</Alert>}
      {list.data && (
        <ResponsiveTable<MailSuppression>
          caption={t('admin.mailDelivery.suppressions')}
          rows={rows}
          rowKey={(row) => row.id}
          empty={t('admin.mailDelivery.noSuppressions')}
          columns={[
            { key: 'address', header: t('admin.mailDelivery.address'), cell: (row) => row.address },
            {
              key: 'reason',
              header: t('admin.mailDelivery.reason'),
              cell: (row) => (
                <div>
                  <Badge variant="warning">{t(`admin.mailDelivery.reasons.${row.reason}`)}</Badge>
                  {row.providerDetail && (
                    <p className="mt-1 text-xs text-muted-foreground">{row.providerDetail}</p>
                  )}
                </div>
              ),
            },
            {
              key: 'since',
              header: t('admin.mailDelivery.since'),
              cell: (row) => formatDate(row.createdAt, i18n.language, true),
            },
            {
              key: 'lift',
              header: '',
              cell: (row) => (
                <Button
                  variant="outline"
                  size="sm"
                  loading={lift.isPending && lift.variables === row.id}
                  onClick={() => lift.mutate(row.id)}
                  data-testid="mail-suppression-lift"
                >
                  {t('admin.mailDelivery.lift')}
                </Button>
              ),
            },
          ]}
        />
      )}
      <LoadMore query={list} />
    </div>
  );
}

function WebhookEvents() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [failedOnly, setFailedOnly] = useState(false);
  const params = { status: failedOnly ? 'FAILED' : undefined };
  const events = useInfiniteQuery({
    queryKey: queryKeys.admin.mailWebhookEvents(params),
    queryFn: ({ pageParam }) =>
      api.admin.mail.webhookEvents({ ...params, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.admin.mail.retryWebhookEvent(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'mail', 'webhook-events'] });
      toast.success(t('admin.mailDelivery.retryQueued'));
    },
    onError: (e) => toastError(e),
  });
  const rows = events.data?.pages.flatMap((p) => p.items) ?? [];
  const variant = {
    RECEIVED: 'outline',
    PROCESSED: 'success',
    FAILED: 'destructive',
    IGNORED: 'outline',
  } as const;
  return (
    <div className="mt-4">
      <label className="mb-3 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={failedOnly}
          onChange={(e) => setFailedOnly(e.target.checked)}
        />
        {t('admin.mailDelivery.failedOnly')}
      </label>
      {events.isLoading && <Spinner label={t('app.loading')} />}
      {events.error && <Alert variant="destructive">{describeError(events.error)}</Alert>}
      {events.data && (
        <ResponsiveTable<WebhookEventEntry>
          caption={t('admin.mailDelivery.events')}
          rows={rows}
          rowKey={(row) => row.id}
          empty={t('admin.mailDelivery.noEvents')}
          columns={[
            {
              key: 'date',
              header: t('admin.mailDelivery.date'),
              cell: (row) => formatDate(row.createdAt, i18n.language, true),
            },
            {
              key: 'type',
              header: t('admin.mailDelivery.eventType'),
              cell: (row) => <code>{row.type}</code>,
            },
            {
              key: 'status',
              header: t('admin.mailDelivery.status'),
              cell: (row) => (
                <div>
                  <Badge variant={variant[row.status]}>
                    {t(`admin.mailDelivery.eventStatuses.${row.status}`)}
                  </Badge>
                  {row.error && (
                    <p className="mt-1 max-w-sm text-xs text-muted-foreground">{row.error}</p>
                  )}
                </div>
              ),
            },
            {
              key: 'attempts',
              header: t('admin.mailDelivery.attempts'),
              cell: (row) => row.attempts,
            },
            {
              key: 'retry',
              header: '',
              cell: (row) =>
                row.status === 'FAILED' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    loading={retry.isPending && retry.variables === row.id}
                    onClick={() => retry.mutate(row.id)}
                  >
                    {t('admin.mailDelivery.retry')}
                  </Button>
                ) : null,
            },
          ]}
        />
      )}
      <LoadMore query={events} />
    </div>
  );
}
